import React from 'react';
import {
  Grid,
  Paper,
  Typography,
  Box,
  Link,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Link as RouterLink } from 'react-router-dom';
import FeatureNavTabs from '../features/shared/components/FeatureNavTabs';

const AboutPage: React.FC = () => {
  return (
    <>
      {/* Full details in accordions with original text */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12}>
          <Typography variant="h6" sx={{ px: 1, pt: 1, mb: 1.5 }}>
            Methodology details
          </Typography>
          <Paper elevation={1} className="content-text" sx={{ p: 2, border: '1px solid', borderColor: (t) => t.palette.divider, bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(0,0,0,0.35)' : 'rgba(255,255,255,0.3)' }}>
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1"  sx={{ fontWeight: 700 }}>The problem</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body1" paragraph sx={{ textAlign: 'justify' }}>
                <Box component="span" sx={{ fontStyle: 'italic', fontWeight: 700 }}>
                    Industrial strategy design is only as good as the empirics and analysis that underpin it.
                  </Box>{' '}
                  Yet, economic analysis methodologies tend to ‘look where the light is’ rather than directing the light toward the concrete, granular data required to build meaningful, actionable policy insights. There are pragmatic reasons for this: Data providers, such as governments and multilateral organizations, generally provide  low-resolution, aggregated data structured in standardized datasets that cater to a maximally generic audience (for example, at its highest resolution, trade data is reported in 6-digit HS codes that can contain hundreds (if not thousands) of technologically distinct products under a single code). Consequently, datasets are almost never fit for specific analytical purposes and analysts are forced to make compromises due to the substantial effort (millions of human labor hours) that would be required to create bespoke datasets from scratch. These limitations hinder the accuracy and precision of academic research and often become fatal bottlenecks in the context of industrial policy design and implementation, which require more fine grained data to identify current, latent, potential, and strategic productive capabilities. Our methodology papers discuss how these limitations constrain the utility of common frameworks and tools for industrial policy and trade analysis:{' '}
                  <Link href="https://www.i3-t.org/documents/Industrial%20strategy%20-%20Methodological%20approaches,%20lessons,%20and%20pitfalls%20-%20i3T%20-%202025.pdf" target="_blank" rel="noopener noreferrer">Estevez, Chang, and Schollmeyer (2025)</Link>{' '}and <Link href="https://accounting-ai.github.io/rubidium-data-publications/pdfs/valuechains-methodology-paper-v-1-0/valuechains-methodology-paper-v-1-0.pdf" target="_blank" rel="noopener noreferrer">Schollmeyer et al. (2025)</Link>.
                </Typography>
              </AccordionDetails>
            </Accordion>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1"  sx={{ fontWeight: 700 }}>Our approach</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body1" paragraph sx={{ textAlign: 'justify' }}>
                <Box component="span" sx={{ fontStyle: 'italic', fontWeight: 700 }}>
                To overcome these long-standing bottlenecks, our Data Lab leverages AI to illuminate granular, concrete, real-world information and construct datasets specifically designed for implementation-ready industrial policy analysis.
                  </Box>{' '}
                    Recent advances in large language models (LLMs) have made it possible to design and deploy legions of AI researchers that mimic highly-trained human researchers to assemble and  structure vast amounts of information. Using these tools, our data team has developed a first-of-its-kind auto-research algorithm that draws on publications, intellectual property rights records, business-to-business price data, targeted web search, and more to discover and characterize productive capabilities at a sufficiently fine level of detail to move from rough analysis to practical policy design and implementation. This includes fine-grained information about productive capabilities, technological dependencies, and value adding processes (firm-level capacities, talent availability, value added, cost, and profit data, resource use, etc.).
                </Typography>
              </AccordionDetails>
            </Accordion>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Pilots</Typography>
              </AccordionSummary>
              <AccordionDetails>
              
                <Typography variant="body1" paragraph sx={{ textAlign: 'justify' }}>
                <Box component="span" sx={{ fontStyle: 'italic', fontWeight: 700 }}>
                Our tools are already being piloted in industrial value chain targeting projects
                  </Box>{' '}
                  with the World Bank, for the Romanian government; and in large scale company surveys with the European Commission and the World Bank: interview-based surveys of 1,964 SMEs from 34 countries in their local languages using our <Link href="https://www.askverbatim.com/" target="_blank" rel="noopener noreferrer">Verbatim</Link> tool.
                </Typography>
              </AccordionDetails>
            </Accordion>
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1"  sx={{ fontWeight: 700 }}>Visualization dashboard</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body1" paragraph sx={{ textAlign: 'justify' }}>
                <Box component="span" sx={{ fontStyle: 'italic', fontWeight: 700 }}>
                Our visualization dashboard uses the solar PV value chain as an illustrative example
                  </Box>{' '}
                  to offer a glimpse into our methodologies for value chain decomposition, technology analysis, trade code mapping, company mapping, competitiveness proxies, and trade balances along the value chain.
                </Typography>
                <Box
                  component="img"
                  src="/visualization-dashboard.gif"
                  alt="Visualization dashboard preview"
                  sx={{
                    width: '100%',
                    maxWidth: 880,
                    display: 'block',
                    margin: '12px auto 0',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: (t) => t.palette.divider,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    opacity: 1,
                  }}
                />
                
               <Typography variant="body1" paragraph sx={{ textAlign: 'justify', mt: 2 }}>
                  The 12,800 components and types are fully navigable (see the gif below). By clicking on any of the components or types, you can access detailed information about, e.g., their function, mechanism, as well as their 6-digit trade code. Moreover, you can navigate upstream to explore the inputs required to produce a specific type. While still experimental and incomplete, this mapping is the first of its kind and we aim to make these detailed breakdowns available for as many value chains as possible in the near future for the benefit of policymakers and researchers. <Link component={RouterLink} to="/systems">Try it yourself</Link>.
                </Typography>
              
              </AccordionDetails>
            </Accordion>
          </Paper>
        </Grid>
      </Grid>
      <FeatureNavTabs />
    </>
  );
};

export default AboutPage;


